'use client';

import { useEffect, useState } from 'react';
import Contentlet from '@/components/shared/contentlet';
import customRenderers from '@/components/block-editor';
import { NewsletterForm } from '@/components/shared/newsletter-form/NewsletterForm';
import TableOfContents from '@/components/shared/table-of-contents/TableOfContents';
import { NewsDetailHeader } from '@/components/newsroom/NewsDetailHeader';
import { NewsRelatedPosts, NewsRelatedPostsSkeleton } from '@/components/newsroom/NewsRelatedPosts';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import { isBlockEditorEmpty } from '@/app/utils';
import { formatDate } from '@/app/utils/formatDate';

const GRAPHQL_URL = `${process.env.NEXT_PUBLIC_DOTCMS_HOST}/api/v1/graphql`;

const TAG_COLORS_MAP = {
    600: 'bg-fuschia-500',
    700: 'bg-fuschia-600',
    800: 'bg-fuschia-700'
};

function buildTags(tags) {
    const arr = Array.isArray(tags) ? tags : typeof tags === 'string' ? tags.split(',') : [];
    return arr
        .map((tag, i) => ({
            text: typeof tag === 'string' ? tag.trim() : tag,
            fontColor: 'text-white',
            bgColor: TAG_COLORS_MAP[Math.max(800 - i * 100, 600)]
        }))
        .slice(0, 3);
}

const buildSuggestionsQuery = (urlTitle) => {
    const safe = urlTitle.replace(/"/g, '');
    return `{
        search(query: "+contentType:News +live:true -News.urlTitle:\\"${safe}\\"") {
            ... on News {
                identifier
                title
                urlTitle
                publishDate
                teaser
                thumbnailAlt
                titleImage { idPath versionPath }
            }
        }
    }`;
};

export function NewsDetail({ data, relatedPosts = [] }) {
    const [contentlet, setContentlet] = useState(data);
    const [suggestedPosts, setSuggestedPosts] = useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(
        relatedPosts.length === 0 && !!data.urlTitle
    );

    useEffect(() => {
        setContentlet(data);
    }, [data]);

    useEffect(() => {
        if (relatedPosts.length > 0 || !data.urlTitle) return;

        setIsLoadingSuggestions(true);

        fetch(GRAPHQL_URL, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${process.env.NEXT_PUBLIC_DOTCMS_AUTH_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query: buildSuggestionsQuery(data.urlTitle) })
        })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((json) => {
                const items = (json?.data?.search ?? []).slice(0, 4);
                setSuggestedPosts(items);
            })
            .catch((err) => console.error('Failed to fetch news suggestions:', err))
            .finally(() => setIsLoadingSuggestions(false));
    }, [data.urlTitle, relatedPosts.length]);

    const { title, body, publishDate, titleImage, thumbnailAlt, tags } = contentlet;
    const postsToShow = relatedPosts.length > 0 ? relatedPosts : suggestedPosts;

    return (
        <main className="flex w-full flex-col gap-0">
            <Contentlet contentlet={contentlet}>
                <NewsDetailHeader
                    title={title}
                    publishDate={formatDate(publishDate)}
                    tags={buildTags(tags)}
                    titleImage={titleImage}
                    thumbnailAlt={thumbnailAlt}
                />
                <div className="p-4 pt-11 md:p-16 lg:px-32">
                    <div className="container flex gap-6 p-0 lg:gap-9">
                        <article className="prose w-full max-w-fit break-words lg:w-2/3">
                            {!isBlockEditorEmpty(body) && (
                                <DotBlockEditor customRenderers={customRenderers} blocks={body} />
                            )}
                        </article>
                        <div
                            className="hidden flex-col gap-5 lg:flex lg:w-1/3"
                            style={{ position: 'sticky', top: '7.5rem', alignSelf: 'flex-start' }}>
                            <TableOfContents />
                            <NewsletterForm />
                        </div>
                    </div>
                </div>
            </Contentlet>
            {isLoadingSuggestions ? (
                <NewsRelatedPostsSkeleton />
            ) : (
                <NewsRelatedPosts posts={postsToShow} />
            )}
        </main>
    );
}
